"use client"

import { Nuisible } from "@repo/types";
import { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, Search, Trash2 } from "lucide-react";
import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { DataTable } from "../../components/ui/data-table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { useDeleteNuisible } from "../../hooks/api/nuisibles";
import { formatDate } from "../../lib/utils";
import { safeReload } from "../../lib/utils/localStorage";

interface NuisibleListProps {
  nuisibles: Nuisible[];
  isLoading: boolean;
  error: any;
  onView: (id: number) => void;
  onEdit: (id: number) => void;
  page: number;
  limit: number;
  total: number;
  sortBy: string;
  onLimitChange: (limit: number) => void;
  onSearchChange: (search: string) => void;
  searchTerm: string;
  onPageChange: (page: number) => void;
  handleSortChange: (sortBy: string) => void;
  showDeleted: boolean;
  pageCount: number;
  onShowDeletedChange: (showDeleted: boolean) => void;
}

export function NuisibleList({
  nuisibles,
  isLoading,
  error,
  onView,
  onEdit,
  page,
  limit,
  total,
  sortBy,
  onLimitChange,
  onSearchChange,
  searchTerm,
  onPageChange,
  handleSortChange,
  showDeleted,
  pageCount,
  onShowDeletedChange
}: NuisibleListProps) {

  const [nuisibleToDelete, setNuisibleToDelete] = useState<Nuisible | null>(null);
  const deleteNuisibleMutation = useDeleteNuisible();

  const handleDeleteClick = (nuisible: Nuisible) => {
    setNuisibleToDelete(nuisible);
  };

  const handleConfirmDelete = async () => {
    if (nuisibleToDelete) {
      try {
        await deleteNuisibleMutation.mutateAsync(nuisibleToDelete.id);
        setNuisibleToDelete(null);
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  const handleCancelDelete = () => {
    setNuisibleToDelete(null);
  };

  // Define columns for the DataTable
  const columns: ColumnDef<Nuisible>[] = [
    {
      accessorKey: "name",
      header: "Nom",
      cell: ({ row }) => <span className="font-medium">{row.original.name || "-"}</span>,
    },
    {
      header: "Date de création",
      accessorKey: "created_at",
      cell: ({ row }) => {
        const date = row.original.created_at;
        return date ? (
          <span className="text-muted-foreground">{formatDate(new Date(date))}</span>
        ) : "-";
      }
    },
    {
      header: "Date de modification",
      accessorKey: "updated_at",
      cell: ({ row }) => {
        const date = row.original.updated_at;
        return date ? (
          <span className="text-muted-foreground">{formatDate(new Date(date))}</span>
        ) : "-";
      }
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const nuisible = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Ouvrir le menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onView(nuisible.id)}>
                Voir les détails
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEdit(nuisible.id)}>
                Modifier
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleDeleteClick(nuisible)}
                className="text-red-600 focus:text-red-600"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center">
            <p className="text-red-600">Erreur lors du chargement des nuisibles</p>
            <Button variant="outline" onClick={() => safeReload()} className="mt-2">
              Réessayer
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <CardHeader className="p-3 pb-2">

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Rechercher des nuisibles..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex items-center gap-4">
            {/* Show Deleted Toggle */}
            <div className="flex items-center space-x-2">
              <Switch
                id="show-deleted"
                checked={showDeleted}
                onCheckedChange={onShowDeletedChange}
              />
              <Label htmlFor="show-deleted" className="text-sm">
                Afficher supprimés
              </Label>
            </div>

            {/* Sort By */}
            <Select value={sortBy} onValueChange={handleSortChange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Trier par" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Plus récent</SelectItem>
                <SelectItem value="oldest">Plus ancien</SelectItem>
                <SelectItem value="name">Nom A-Z</SelectItem>
                <SelectItem value="name_desc">Nom Z-A</SelectItem>
              </SelectContent>
            </Select>


          </div>
        </div>
      </CardHeader>

      <CardContent className="p-3 pt-0">
        <DataTable
          serverSidePagination
          density="compact"
          stickyPagination
          columns={columns}
          data={nuisibles}
          isLoading={isLoading}
          renderSearchOutside={true}
          externalGlobalFilter={searchTerm}
          onGlobalFilterChange={onSearchChange}
          pageCount={pageCount}
          pageSize={limit}
          totalCount={total}
          onPageChange={onPageChange}
          onPageSizeChange={onLimitChange}
          searchPlaceholder="Rechercher un nuisible..."
          currentPage={page}
        />



        {/* Summary */}
        <div className="flex items-center justify-between text-sm text-muted-foreground mt-2">
          <div>
            Affichage de {((page - 1) * limit) + 1} à {Math.min(page * limit, total)} sur {total} nuisible(s)
          </div>
        </div>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!nuisibleToDelete} onOpenChange={() => setNuisibleToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer le nuisible "{nuisibleToDelete?.name}" ?
              Cette action ne peut pas être annulée.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelDelete}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteNuisibleMutation.isPending}
            >
              {deleteNuisibleMutation.isPending ? "Suppression..." : "Supprimer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
